<template>
  <div>
    <!-- NEW: Recovery ROI layout (flag enabled) -->
    <div v-if="isRecoveryRoiEnabled && !loading">
      <!-- PRIMARY: Revenue Saved -->
      <div class="main-text text-center">{{ formattedRevenue }}</div>
      <div style="min-height: 28.5px">
        <div
          class="mt-2 text-center"
          :style="{ color: revenueComparisonColor }"
          v-if="hasRevenueComparison"
        >
          <span
            v-if="revenueComparisonColor === 'black'"
            class="text-muted"
            style="font-size: 14px"
          >
            {{ revenueComparisonText }}
          </span>
          <span v-else>
            <span v-if="isRevenuePositive">
              <i class="fas fa-caret-up" />
            </span>
            <span v-else>
              <i class="fas fa-caret-down" />
            </span>
            <span class="ml-1"
              >{{ formattedRevenueChange }} <span class="text-muted">vs last period</span></span
            >
          </span>
        </div>
        <div class="mt-2" style="height: 22.5px" v-else></div>
      </div>

      <!-- Customers Saved -->
      <div class="metric-section mt-3">
        <div class="metric-label text-center text-muted">Customers Saved</div>
        <div class="main-text text-center">{{ customersSavedCount }}</div>
        <div style="min-height: 28.5px">
          <div
            class="mt-1 text-center"
            :style="{ color: customerComparisonColor }"
            v-if="hasCustomerComparison"
          >
            <span
              v-if="customerComparisonColor === 'black'"
              class="text-muted"
              style="font-size: 14px"
            >
              {{ customerComparisonText }}
            </span>
            <span v-else>
              <span v-if="isCustomerPositive">
                <i class="fas fa-caret-up" />
              </span>
              <span v-else>
                <i class="fas fa-caret-down" />
              </span>
              <span class="ml-1"
                >{{ formattedCustomerChange }} <span class="text-muted">vs last period</span></span
              >
            </span>
          </div>
          <div class="mt-1" style="height: 22.5px" v-else></div>
        </div>
      </div>
    </div>

    <!-- LEGACY: Customers Saved layout (flag disabled) -->
    <div v-else-if="!loading">
      <ValueComparison :value="legacyValue" :comparison="legacyComparison" />
      <AreaGraph
        :dataSet="selectTrendData('customersSavedCount')"
        :valueType="'integer'"
        v-if="!!selectTrendData('customersSavedCount')"
        :granularity="selectGranularity"
      />
    </div>

    <ValueComparisonLoader v-else />
  </div>
</template>

<script>
import ValueComparison from '@/components/Modules/Dashboard/Widget/ReportTypes/ValueComparison'
import ValueComparisonLoader from '@/components/Modules/Dashboard/Widget/SkeletonLoaders/ValueComparisonLoader'
import AreaGraph from '@/components/Modules/Dashboard/Widget/ReportTypes/AreaGraph'
import { createNamespacedHelpers } from 'vuex'

const DashboardModule = createNamespacedHelpers('dashboard')
const FeatureFlagModule = createNamespacedHelpers('featureFlag')

export default {
  name: 'CustomerSavedCount',
  props: ['widgetId'],
  components: {
    ValueComparison,
    ValueComparisonLoader,
    AreaGraph
  },
  data: () => ({
    loading: true
  }),
  computed: {
    ...DashboardModule.mapState(['widgetData']),
    ...DashboardModule.mapGetters(['selectTrendData', 'selectGranularity']),
    ...FeatureFlagModule.mapGetters(['isEnabledV2']),

    isRecoveryRoiEnabled() {
      return this.isEnabledV2('RECOVERY_ROI_DASHBOARD_WIDGET')
    },

    currentWidgetData() {
      return this.widgetData[this.widgetId] || {}
    },

    // Revenue values
    revenueSaved() {
      return this.currentWidgetData.value?.revenueSaved || 0
    },
    previousRevenueSaved() {
      return this.currentWidgetData.comparison?.previousRevenueSaved || 0
    },

    // Customer values
    customersSavedCount() {
      return this.currentWidgetData.value?.customersSavedCount || 0
    },
    previousCustomersSavedCount() {
      return this.currentWidgetData.comparison?.previousCustomersSavedCount || 0
    },

    // Formatted revenue
    formattedRevenue() {
      return this.formatCurrency(this.revenueSaved)
    },

    // Revenue comparison
    revenueChangePercent() {
      if (this.previousRevenueSaved === 0) return null
      return ((this.revenueSaved - this.previousRevenueSaved) / this.previousRevenueSaved) * 100
    },
    hasRevenueComparison() {
      return this.revenueSaved > 0
    },
    isRevenuePositive() {
      return this.revenueChangePercent !== null && this.revenueChangePercent >= 0
    },
    revenueComparisonColor() {
      if (!this.revenueChangePercent) return 'black'
      return this.revenueChangePercent > 0 ? 'green' : 'red'
    },
    revenueComparisonText() {
      if (this.revenueChangePercent === null) return 'No data last period'
      return 'Same as last period'
    },
    formattedRevenueChange() {
      if (this.revenueChangePercent === null) return ''
      const absPercent = Math.abs(Math.round(this.revenueChangePercent))
      return `${absPercent}%`
    },

    // Customer comparison
    customerChangePercent() {
      if (this.previousCustomersSavedCount === 0) return null
      return (
        ((this.customersSavedCount - this.previousCustomersSavedCount) /
          this.previousCustomersSavedCount) *
        100
      )
    },
    hasCustomerComparison() {
      return this.customersSavedCount > 0
    },
    isCustomerPositive() {
      return this.customerChangePercent !== null && this.customerChangePercent >= 0
    },
    customerComparisonColor() {
      if (!this.customerChangePercent) return 'black'
      return this.customerChangePercent > 0 ? 'green' : 'red'
    },
    customerComparisonText() {
      if (this.customerChangePercent === null) return 'No data last period'
      return 'Same as last period'
    },
    formattedCustomerChange() {
      if (this.customerChangePercent === null) return ''
      const absPercent = Math.abs(Math.round(this.customerChangePercent))
      return `${absPercent}%`
    },

    // Legacy layout computed values (flag disabled)
    legacyValue() {
      return this.customersSavedCount.toString()
    },
    legacyComparison() {
      const value = this.customersSavedCount
      const comparison = this.previousCustomersSavedCount
      return comparison > 0
        ? Math.round(((value - comparison) / comparison) * 100).toString() + '%'
        : value === 0
        ? '0'
        : ''
    }
  },
  methods: {
    ...DashboardModule.mapActions(['fetchCustomerSavedCount']),

    formatCurrency(value) {
      if (value === null || value === undefined) return '$0'
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value)
    }
  },
  async mounted() {
    await this.fetchCustomerSavedCount({ widgetId: this.widgetId })
    this.loading = false
  }
}
</script>

<style scoped>
.main-text {
  font-size: 21px;
  font-weight: bold;
}

.metric-section {
  border-top: 1px solid #eee;
  padding-top: 0.75rem;
}

.metric-label {
  font-size: 14px;
  margin-bottom: 0.25rem;
}
</style>
